type BrowserAudioContext = AudioContext & { resume(): Promise<void> }

let sharedContext: BrowserAudioContext | null = null

const audioContextConstructor = () => {
  const browserWindow = window as typeof window & {
    webkitAudioContext?: typeof AudioContext
  }
  return window.AudioContext ?? browserWindow.webkitAudioContext
}

export const prepareRewardSound = (): BrowserAudioContext | null => {
  try {
    const AudioContextClass = audioContextConstructor()
    if (!AudioContextClass) return null
    sharedContext ??= new AudioContextClass() as BrowserAudioContext
    if (sharedContext.state === 'suspended') void sharedContext.resume().catch(() => undefined)
    return sharedContext
  } catch {
    return null
  }
}

const addChime = (
  context: BrowserAudioContext,
  destination: AudioNode,
  frequency: number,
  startAt: number,
  duration: number,
  volume: number,
) => {
  const oscillator = context.createOscillator()
  const gain = context.createGain()
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(frequency, startAt)
  gain.gain.setValueAtTime(0.0001, startAt)
  gain.gain.exponentialRampToValueAtTime(volume, startAt + 0.025)
  gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration)
  oscillator.connect(gain).connect(destination)
  oscillator.start(startAt)
  oscillator.stop(startAt + duration + 0.03)
}

export const playTreasureOpenSound = (context: BrowserAudioContext | null) => {
  if (!context) return
  try {
    const startAt = context.currentTime + 0.015
    const master = context.createGain()
    master.gain.setValueAtTime(0.75, startAt)
    master.connect(context.destination)

    // A short rising pentatonic phrase creates a bright, reward-like “shalaan”.
    const notes = [659.25, 783.99, 987.77, 1318.51]
    notes.forEach((frequency, index) => {
      addChime(context, master, frequency, startAt + index * 0.115, 0.55, 0.16)
      addChime(context, master, frequency * 2, startAt + index * 0.115 + 0.035, 0.38, 0.045)
    })
    addChime(context, master, 1567.98, startAt + 0.52, 0.72, 0.11)
  } catch {
    // Sound is an optional effect; treasure persistence must never depend on it.
  }
}

export const playBoardStepSound = (context: BrowserAudioContext | null, position: number) => {
  if (!context) return
  try {
    const startAt = context.currentTime + 0.01
    const master = context.createGain()
    master.gain.setValueAtTime(0.5, startAt)
    master.connect(context.destination)

    // Alternate two soft wooden notes so consecutive squares feel like footsteps.
    const frequency = position % 2 === 0 ? 440 : 523.25
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'triangle'
    oscillator.frequency.setValueAtTime(frequency, startAt)
    oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.82, startAt + 0.09)
    gain.gain.setValueAtTime(0.0001, startAt)
    gain.gain.exponentialRampToValueAtTime(0.11, startAt + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.12)
    oscillator.connect(gain).connect(master)
    oscillator.start(startAt)
    oscillator.stop(startAt + 0.14)
  } catch {
    // Board movement must still work when audio is unavailable.
  }
}

export const playBoardEventSound = (context: BrowserAudioContext | null, eventType: string) => {
  if (!context) return
  try {
    const startAt = context.currentTime + 0.015
    const master = context.createGain()
    master.gain.setValueAtTime(0.62, startAt)
    master.connect(context.destination)

    const notes = eventType === 'half_next'
      ? [523.25, 392]
      : eventType === 'barrier'
        ? [392, 523.25, 659.25]
        : eventType === 'minimum_3_next'
          ? [440, 554.37, 659.25]
          : [659.25, 830.61, 987.77]
    notes.forEach((frequency, index) => {
      addChime(context, master, frequency, startAt + index * 0.105, 0.38, 0.12)
    })
  } catch {
    // Event feedback is optional and must not affect the saved movement.
  }
}

export const playBoardGoalSound = (context: BrowserAudioContext | null) => {
  if (!context) return
  try {
    const startAt = context.currentTime + 0.015
    const master = context.createGain()
    master.gain.setValueAtTime(0.68, startAt)
    master.connect(context.destination)
    ;[523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
      addChime(context, master, frequency, startAt + index * 0.12, 0.58, 0.14)
    })
  } catch {
    // Goal persistence and reward issuance never depend on sound playback.
  }
}

export const shouldPlayTreasureSound = (status: number) => status === 201
